/**
 * Pip and Mallow wardrobe pieces (batch-003 `pieces.ts`; art identical, layout expanded).
 * Coordinates are body-rig local before GhostRenderer's body-size and pose transforms, which apply once to every layer.
 */
import { buildStaticAsset } from '../../../shared/assets/hushStatic';
import { el, n } from '../../../shared/assets/hushStatic/construction';
import type { ResolvedPalette } from '../../../shared/assets/hushStatic/contracts';
import { box, corners } from '../../../shared/assets/hushStatic/geometry';
import { SeededRng } from '../../../shared/utils/SeededRng';
import type { ShapeType } from '../AppearanceDefinitions';
import { BODY_RIGS, type BodyRig } from '../parts/HumanoidGeometry';
import type { ExtrasContext } from '../parts/humanoid/HumanoidExtras';
import { legacyPainter } from '../parts/humanoid/NpcFinish';
import { IDENTITY, apply, attachmentMatrix, mallowSeaGlass } from './geometry';
import { headFitMatrix } from './rig';
import type { AccessoryPiece, HushWardrobeConfig, Point2, StaticRecipe, VisualBounds } from './types';

type Painter = NonNullable<ExtrasContext['painter']>;
interface Build { c: HushWardrobeConfig; p: ResolvedPalette; rig: BodyRig; paint: Painter; shape: ShapeType; ctx: ExtrasContext; pieces: AccessoryPiece[] }

function fixed(b: Build, id: string, body: string, bounds: VisualBounds): void {
  b.pieces.push({
    id, slot: 'worn', body, paintBounds: bounds, coordinateSpace: 'body-rig', portraitRequired: false,
    source: 'hush-wardrobe-candidate', anchor: { name: 'torso_front', point: { x: b.rig.centerX, y: 70 } }, matrix: IDENTITY,
  });
}

function pipCap(b: Build): void {
  const { p, c, paint, rig, shape } = b;
  const r = c.recipe;
  const cap = 'M31 26Q29 13 49 10L72 5L79 14Q92 16 94 27L82 25Q57 22 31 26Z';
  let body = el('path', { 'data-hush-piece': 'pip-cap', d: cap, fill: paint.fill(p.woodBase), stroke: p.outline, 'stroke-width': 1.3, 'stroke-linejoin': 'round' });
  body += el('path', { d: 'M49 10L72 5L67 15L80 18Q56 15 34 23Z', fill: p.woodShadow, opacity: 0.55 });
  body += el('path', { 'data-hush-piece': 'pip-cap-hem', d: 'M31 22Q59 17 94 24L94 27Q60 23 31 26Z', fill: p.woodLight, stroke: p.woodShadow, 'stroke-width': 0.7 });
  body += el('path', { 'data-hush-piece': 'pip-cap-repair', d: 'M42 15L51 13L54 20L44 22Z', fill: p.paperShadow, stroke: p.woodShadow, 'stroke-width': 0.65 });
  const stitch = 0.5 + new SeededRng(`${r.seed}|cap-repair`).next();
  if (r.detail === 'close') body += el('path', { d: 'M59 11Q66 15 74 16', fill: 'none', stroke: p.woodLight, 'stroke-width': 0.6 });
  if (r.detail !== 'readable') body += el('path', { d: `M44 ${n(16 + stitch)}l1 3m3 -4l1 3`, stroke: p.paperLight, 'stroke-width': 0.7 });
  const hm = headFitMatrix(shape);
  const bounds = box(corners({ x: 29.5, y: 4.3, width: 65.2, height: 23.4 }).map((v) => apply(hm, v)));
  b.pieces.push({
    id: 'pip-cap', slot: 'head', body, paintBounds: bounds, coordinateSpace: 'classic-head', portraitRequired: true,
    source: 'classic-head accessory mapped by current headFitTransform',
    anchor: { name: 'head_top', point: { x: (rig.head.outerLeft + rig.head.outerRight) / 2, y: rig.head.topY } }, matrix: hm,
  });
}

function pipWrapAndRibbon(b: Build): void {
  const { p, c, paint, rig } = b;
  const r = c.recipe;
  const l = rig.torso.neck.left - 3;
  const rr = rig.torso.neck.right + 1;
  fixed(b, 'pip-blue-wrap', el('path', {
    'data-hush-piece': 'pip-blue-wrap', d: `M${l} 67Q${rig.centerX} 73 ${rr} 67L${rr} 72Q${rig.centerX} 78 ${l} 72Z`,
    fill: paint.fill(p.clothBlue), stroke: p.clothBlueShadow, 'stroke-width': 0.8,
  }), { x: l - 0.5, y: 66.5, width: rr - l + 1, height: 12 });
  const ribbonPose = r.params.pose === 'gesture' ? 'right_breeze' : r.params.pose === 'talk' ? 'left_breeze' : 'rest';
  const ribbonRecipe = {
    ...c.resources.ribbon, id: 'hush.ribbon.pip_wardrobe.v1', seed: r.seed, paletteId: r.paletteId, detail: r.detail,
    params: { ...c.resources.ribbon.params, tailLength: 40, width: 10, pose: ribbonPose },
  } as StaticRecipe;
  const ribbon = buildStaticAsset(ribbonRecipe, c.tokens, 'hush_wardrobe_ribbon');
  const target = { x: rig.torso.neck.right - 1, y: 74 };
  const rm = attachmentMatrix(target, { x: 0, y: 0 }, 0.45, 0);
  b.pieces.push({
    id: 'pip-ribbon', slot: 'worn', body: el('g', { 'data-hush-piece': 'pip-ribbon', transform: `matrix(${rm.map(n).join(' ')})` }, ribbon.body),
    defs: ribbon.defs, paintBounds: box(corners(ribbon.paintBounds).map((v) => apply(rm, v))), coordinateSpace: 'body-rig',
    portraitRequired: false, source: 'frozen-static-kit/hush.ribbon.v1', anchor: { name: 'neck', point: target }, matrix: rm,
  });
}

function pipBoat(b: Build): void {
  const { c, ctx } = b;
  const r = c.recipe;
  const boatRecipe = { ...c.resources.boat, id: 'hush.boat.pip_held.v1', seed: r.seed, paletteId: r.paletteId, detail: r.detail } as StaticRecipe;
  const boat = buildStaticAsset(boatRecipe, c.tokens, 'hush_wardrobe_boat');
  // Rest the real left keel corner on the front palm, instead of hiding the boat in the fist.
  const grip = (boat.geometry.hull as Point2[])[1];
  const palm = { x: ctx.frontHand.x + 2, y: ctx.frontHand.y - 4 };
  const scale = ctx.frontHand.y < 67 ? 0.18 : Math.min(0.31, (138 - ctx.frontHand.x - 2) / 78);
  const angle = r.params.pose === 'gesture' ? -4 : 0;
  const bm = attachmentMatrix(palm, grip, scale, angle);
  const held = boat.body.replace(/<ellipse[^>]*data-part="contact-shadow"[^>]*\/>/, '');
  b.pieces.push({
    id: 'pip-paper-boat', slot: 'worn',
    body: el('g', { 'data-hush-piece': 'pip-paper-boat', 'data-support': 'frontHand', transform: `matrix(${bm.map(n).join(' ')})` }, held),
    defs: boat.defs, paintBounds: box(corners(boat.paintBounds).map((v) => apply(bm, v))), coordinateSpace: 'body-rig',
    portraitRequired: true, source: 'frozen-static-kit/hush.paper_boat.v1:left-keel-contact', anchor: { name: 'front_palm', point: palm }, matrix: bm,
  });
}

function mallowShawl(b: Build): void {
  const { p, c, paint, rig } = b;
  const r = c.recipe;
  const l = rig.torso.shoulders.left + 5;
  const rr = rig.torso.shoulders.right - 3;
  const cx = rig.centerX;
  fixed(b, 'mallow-shawl-fold',
    el('path', {
      'data-hush-piece': 'mallow-shawl-fold',
      d: `M${l} 67Q${cx} 78 ${rr} 67L${rr + 1} 82L${rr - 10} 99L${rr - 26} 90L${rr - 22} 81Q${cx} 88 ${l - 1} 75Z`,
      fill: paint.fill(mallowSeaGlass(p)), stroke: p.reedShadow, 'stroke-width': 0.8,
    }) + el('path', { d: `M${rr - 5} 77L${rr - 10} 95L${rr - 22} 89`, fill: 'none', stroke: p.reedLight, 'stroke-width': 1.1 }),
    { x: l - 1.5, y: 66.5, width: rr - l + 3, height: 33 });
  if (r.detail !== 'readable') {
    const dy = new SeededRng(`${r.seed}|shawl-seam`).next() * 1.4;
    fixed(b, 'mallow-shawl-stitches', el('path', {
      d: `M${rr - 9} ${n(84 + dy)}l-2 2m1 3l-2 2${r.detail === 'close' ? 'm-2 -2l-2 2' : ''}`, fill: 'none', stroke: p.reedLight, 'stroke-width': 0.7,
    }), { x: rr - 16, y: 83, width: 9, height: 11 });
  }
  fixed(b, 'mallow-brass-clasp', el('g', { 'data-hush-piece': 'mallow-brass-clasp' },
    el('circle', { cx, cy: 70, r: 4.2, fill: p.paperShadow, stroke: p.woodShadow, 'stroke-width': 0.8 })
    + el('circle', { cx: cx - 1, cy: 68.9, r: 1.2, fill: p.paperLight })
    + el('path', { d: `M${cx - 1} 70l2 1`, stroke: p.woodShadow, 'stroke-width': 0.6 })), { x: cx - 4.6, y: 65.4, width: 9.2, height: 9.2 });
}

function mallowStone(b: Build): void {
  const { p, c, paint, ctx } = b;
  const grip = { x: -5, y: 5 };
  const sm = attachmentMatrix(ctx.frontHand, grip, 1, c.recipe.params.pose === 'gesture' ? 8 : 0);
  const stone = el('g', { 'data-hush-piece': 'mallow-smooth-stone', 'data-support': 'frontHand', transform: `matrix(${sm.map(n).join(' ')})` },
    el('path', { d: 'M-9 0Q-8 -8 1 -8Q9 -8 11 -2Q14 4 6 6L-5 6Q-10 5 -9 0Z', fill: paint.fill(p.stoneBase), stroke: p.stoneShadow, 'stroke-width': 0.8 })
    + el('path', { d: 'M-5 -3Q1 -6 7 -3', fill: 'none', stroke: p.stoneLight, 'stroke-width': 1.3, 'stroke-linecap': 'round' }));
  b.pieces.push({
    id: 'mallow-smooth-stone', slot: 'worn', body: stone,
    paintBounds: box(corners({ x: -10.5, y: -8.5, width: 25, height: 15 }).map((v) => apply(sm, v))), coordinateSpace: 'body-rig',
    portraitRequired: true, source: 'simple-smooth-held-prop/current-extras-layer', anchor: { name: 'front_hand', point: ctx.frontHand }, matrix: sm,
  });
}

export function buildWardrobePieces(c: HushWardrobeConfig, shape: ShapeType, ctx: ExtrasContext): AccessoryPiece[] {
  const rig = BODY_RIGS[shape];
  if (!rig) throw new Error('unsupported body shape');
  for (const [label, point] of Object.entries({ frontHand: ctx.frontHand, backHand: ctx.backHand ?? ctx.frontHand })) {
    if (!point || !Number.isFinite(point.x) || !Number.isFinite(point.y)) throw new Error(`invalid ${label} support`);
  }
  if (!ctx.fit) throw new Error('wardrobe requires current fitted rig');
  const b: Build = { c, p: c.tokens.palettes[c.recipe.paletteId], rig, paint: ctx.painter ?? legacyPainter, shape, ctx, pieces: [] };
  if (c.recipe.params.look === 'pip') {
    pipCap(b);
    pipWrapAndRibbon(b);
    pipBoat(b);
  } else {
    mallowShawl(b);
    mallowStone(b);
  }
  return b.pieces;
}
